// pages/Customers/EditCustomer.jsx
// Edit Customer - Same full-page layout as EditUser.js but for CUSTOMER type

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  FaArrowLeft, FaSave, FaStore, FaMapMarkerAlt,
  FaUserTag, FaLock, FaSpinner, FaCheckCircle,
  FaReceipt, FaTruck, FaUserEdit
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosConfig';
import './Customers.css';

const EditCustomer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  // Get shop ID from URL path
  const pathMatch = location.pathname.match(/\/shop\/(\d+)\/edit-customer/);
  const shopIdFromPath = pathMatch ? pathMatch[1] : null;

  // returnTo param for redirect after save
  const urlParams = new URLSearchParams(location.search);
  const returnTo = urlParams.get('returnTo') || 'customers';

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [shops, setShops] = useState([]);
  const [loadingShops, setLoadingShops] = useState(true);

  // City selection
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const [formData, setFormData] = useState({
    id: '',
    first_Name: '',
    last_Name: '',
    email: '',
    phone_Number: '',
    type: 'CUSTOMER',
    is_active: true,
    shop_id: '',
    selected_city: '',
    apply_vat: false,
    apply_delivery_charges: false,
    address: '',
    company_name: '',
    company_size: '',
    Vat_number: ''
  });

  const [selectedShop, setSelectedShop] = useState('');

  // =========================
  // FETCH USER + SHOPS
  // =========================
  useEffect(() => {
    if (id) {
      fetchCustomerData();
    } else {
      setError('Customer ID not found');
      setFetching(false);
    }
    fetchShops();
  }, [id]);

  // Fetch cities when shop changes
  useEffect(() => {
    if (selectedShop) {
      fetchShopCities(selectedShop);
    }
  }, [selectedShop]);

  const fetchCustomerData = async () => {
    try {
      setFetching(true);
      setError(null);

      const response = await axiosInstance.get(`/user/users/${id}/`);
      const user = response.data;

      setFormData({
        id: user.id || '',
        first_Name: user.first_Name || '',
        last_Name: user.last_Name || '',
        email: user.email || '',
        phone_Number: user.phone_Number || '',
        type: user.type || 'CUSTOMER',
        is_active: user.is_active !== undefined ? user.is_active : true,
        shop_id: user.shop || '',
        selected_city: user.selected_city || '',
        apply_vat: !!user.apply_vat,
        apply_delivery_charges: !!user.apply_delivery_charges,
        address: user.address || '',
        company_name: user.company_name || '',
        company_size: user.company_size || '',
        Vat_number: user.Vat_number || ''
      });

      setSelectedShop(user.shop ? String(user.shop) : '');

      if (user.shop) {
        await fetchShopCities(String(user.shop));
      }
    } catch (err) {
      console.error('Error fetching customer:', err);

      if (err.response?.status === 404) {
        setError('Customer not found');
      } else if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        setTimeout(() => navigate('/signin'), 2000);
      } else {
        setError('Failed to load customer data. Please try again.');
      }
    } finally {
      setFetching(false);
    }
  };

  const fetchShops = async () => {
    try {
      setLoadingShops(true);
      const response = await axiosInstance.get('/user/shops/');
      setShops(response.data);
    } catch (err) {
      console.error('Error fetching shops:', err);
    } finally {
      setLoadingShops(false);
    }
  };

  const fetchShopCities = async (shopId) => {
    if (!shopId) {
      setCities([]);
      return;
    }

    try {
      setLoadingCities(true);
      const response = await axiosInstance.get(`/user/shop-cities/?shop_id=${shopId}`);

      if (response.data && response.data.cities) {
        setCities(Array.isArray(response.data.cities) ? response.data.cities : []);
      } else if (Array.isArray(response.data)) {
        setCities(response.data);
      } else {
        setCities([]);
      }
    } catch (err) {
      console.error('Error fetching shop cities:', err);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleShopChange = (e) => {
    const newShopId = e.target.value;
    setSelectedShop(newShopId);
    setFormData(prev => ({
      ...prev,
      shop_id: newShopId,
      selected_city: ''
    }));
    setCities([]);
  };

  // =========================
  // VALIDATION
  // =========================
  const validateForm = () => {
    if (!formData.first_Name?.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'First name is required',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    if (!formData.last_Name?.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Last name is required',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    if (!formData.email?.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Email is required',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please enter a valid email address',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    if (!selectedShop) {
      Swal.fire({
        icon: 'warning',
        title: 'Shop Required',
        text: 'Please select a shop for this customer',
        timer: 2000,
        showConfirmButton: true
      });
      return false;
    }

    return true;
  };

  // =========================
  // SUBMIT
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    const customerData = {
      first_Name: formData.first_Name.trim(),
      last_Name: formData.last_Name.trim(),
      email: formData.email.trim(),
      phone_Number: formData.phone_Number || '',
      type: 'CUSTOMER',
      shop_id: selectedShop || null,
      address: formData.address || '',
      company_name: formData.company_name || '',
      company_size: formData.company_size || '',
      Vat_number: formData.Vat_number || '',
      is_active: formData.is_active,
      selected_city: formData.selected_city || '',
      apply_vat: !!formData.apply_vat,
      apply_delivery_charges: !!formData.apply_delivery_charges
    };

    try {
      const response = await axiosInstance.put(`/user/users/${id}/`, customerData);

      if (response.status === 200) {
        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `Customer ${formData.first_Name} ${formData.last_Name} updated successfully!`,
          timer: 2500,
          showConfirmButton: true
        });

        // Redirect back to customers list
        if (selectedShop) {
          navigate(`/shop/${selectedShop}/customers`);
        } else {
          navigate('/customers');
        }
      }
    } catch (err) {
      console.error('Error updating customer:', err);
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to update customer';
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg,
        timer: 3000,
        showConfirmButton: true
      });
    } finally {
      setLoading(false);
    }
  };

  const currentShop = shops.find(s => s.shop_id === parseInt(selectedShop));

  // =========================
  // ERROR STATE
  // =========================
  if (error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="btn-cancel" onClick={() => navigate('/customers')}>
            Back to Customers
          </button>
        </div>
      </div>
    );
  }


  // =========================
  // MAIN RENDER
  // =========================
  return (
    <div className="add-user-page">
      {/* Header with back button */}
      <div className="add-user-header">
        <button
          className="back-button"
          onClick={() =>
            selectedShop
              ? navigate(`/shop/${selectedShop}/customers`)
              : navigate('/customers')
          }
        >
          <FaArrowLeft /> Back to Customers
        </button>
        <div className="header-title">
          <h1>Edit Customer</h1>
        </div>
        <p className="header-subtitle">
          Update customer account information and billing preferences
        </p>
      </div>

      <form onSubmit={handleSubmit} className="add-user-form-full">
        <div className="form-two-columns">

          {/* ==================== LEFT COLUMN ==================== */}
          <div className="form-column">

            {/* Account Information */}
            <div className="form-section">
              <h3>
                <FaLock className="section-icon" />
                Account Information
              </h3>

              <div className="form-group">
                <label>Email: <span className="required">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="customer@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>User Type:</label>
                <input
                  type="text"
                  value="Customer"
                  disabled
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    background: '#f5f5f5',
                    color: '#666'
                  }}
                />
                <p className="field-hint">
                  Customer has access to shop services and can make purchases
                </p>
              </div>
            </div>

            {/* Shop Assignment */}
            <div className="form-section">
              <h3>
                <FaStore className="section-icon" />
                Shop Assignment
              </h3>

              <div className="form-group">
                <label>Select Shop: <span className="required">*</span></label>
                {loadingShops ? (
                  <div className="loading-select">
                    <FaSpinner className="spinner" /> Loading shops...
                  </div>
                ) : (
                  <select
                    value={selectedShop}
                    onChange={handleShopChange}
                    className="form-select"
                    required
                  >
                    <option value="">Select a shop</option>
                    {shops.map(shop => (
                      <option key={shop.shop_id} value={shop.shop_id}>
                        {shop.shop_name} ({shop.subdomain}.{shop.domain})
                      </option>
                    ))}
                  </select>
                )}
                {shopIdFromPath && currentShop && (
                  <p className="field-hint info-hint">
                    <FaStore /> Shop: {currentShop.shop_name}
                  </p>
                )}
              </div>
            </div>

            {/* City Selection */}
            <div className="form-section">
              <h3>
                <FaMapMarkerAlt className="section-icon" />
                City Selection
                <span className="info-hint"> (Optional)</span>
              </h3>

              <div className="form-group">
                <label>Select City:</label>

                {loadingCities ? (
                  <div className="loading-select">
                    <FaSpinner className="spinner" /> Loading cities...
                  </div>
                ) : (
                  <select
                    name="selected_city"
                    value={formData.selected_city || ''}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={!selectedShop || cities.length === 0}
                  >
                    <option value="">-- Select a city --</option>
                    {cities.map((city, index) => (
                      <option key={index} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                )}

                {!selectedShop && (
                  <p className="field-hint warning-hint">
                    <FaStore /> Please select a shop first to see available cities
                  </p>
                )}

                {selectedShop && cities.length === 0 && !loadingCities && (
                  <p className="field-hint warning-hint">
                    <FaMapMarkerAlt /> No cities available for this shop
                  </p>
                )}

                {selectedShop && cities.length > 0 && (
                  <p className="field-hint success-hint">
                    <FaCheckCircle /> {cities.length} city(ies) available
                  </p>
                )}
              </div>
            </div>

            {/* Billing Section */}
            <div className="form-section">
              <h3>
                <FaReceipt className="section-icon" />
                Billing
              </h3>

              <div className="billing-flags-group">
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="apply_vat"
                      checked={formData.apply_vat}
                      onChange={handleInputChange}
                    />
                    <span className="checkbox-label">
                      <FaReceipt /> Apply VAT
                    </span>
                  </label>
                  <p className="field-hint">
                    If checked, VAT will be added to this customer's order total when they order from their shop.
                  </p>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="apply_delivery_charges"
                      checked={formData.apply_delivery_charges}
                      onChange={handleInputChange}
                    />
                    <span className="checkbox-label">
                      <FaTruck /> Apply Delivery Charges
                    </span>
                  </label>
                  <p className="field-hint">
                    If checked, delivery charges will be added to this customer's order total when they order from their shop.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ==================== RIGHT COLUMN ==================== */}
          <div className="form-column">

            {/* Basic Information */}
            <div className="form-section">
              <h3>
                <FaUserTag className="section-icon" />
                Basic Information
              </h3>

              <div className="form-row">
                <div className="form-group half">
                  <label>First Name: <span className="required">*</span></label>
                  <input
                    type="text"
                    name="first_Name"
                    value={formData.first_Name}
                    onChange={handleInputChange}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="form-group half">
                  <label>Last Name: <span className="required">*</span></label>
                  <input
                    type="text"
                    name="last_Name"
                    value={formData.last_Name}
                    onChange={handleInputChange}
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Phone Number:</label>
                <input
                  type="tel"
                  name="phone_Number"
                  value={formData.phone_Number}
                  onChange={handleInputChange}
                  placeholder="Enter Phone Number"
                />
              </div>

              <div className="form-group">
                <label>Address:</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter customer address"
                  rows="3"
                  className="manual-address-textarea"
                />
              </div>
            </div>

            {/* Company Information */}
            <div className="form-section">
              <h3>
                <FaUserEdit className="section-icon" />
                Company Information
              </h3>

              <div className="form-group">
                <label>Company Name:</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  placeholder="Company name"
                />
              </div>

              <div className="form-group">
                <label>VAT Number:</label>
                <input
                  type="text"
                  name="Vat_number"
                  value={formData.Vat_number}
                  onChange={handleInputChange}
                  placeholder="VAT number"
                />
              </div>
            </div>

            {/* Status */}
            <div className="form-section">
              <h3>Status</h3>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  <span className="checkbox-label">Active Account</span>
                </label>
                <p className="field-hint">
                  Inactive customers cannot login to the system
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions-full">
          <button type="submit" className="btn-save" disabled={loading}>
            {loading ? <FaSpinner className="spinner" /> : <FaSave />}
            {loading ? 'UPDATING...' : 'UPDATE CUSTOMER'}
          </button>
          <button
            type="button"
            className="btn-cancel"
            onClick={() =>
              selectedShop
                ? navigate(`/shop/${selectedShop}/customers`)
                : navigate('/customers')
            }
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCustomer;